import { Page, EntityName, PageSkeleton } from '@/components';
import { useApi } from '@/api';
import { useSearchParams } from '@/hooks';
import { useParams } from 'react-router';
import {
  Flex,
  KpiCard,
  CurrencyValue,
  CryptoIcon,
  Heading,
  formatPercentage,
  KpiSkeleton,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useNetwork } from '@/hooks';
import HistoricGraphs from './HistoricGraphs';
import CollateralActivity from './CollateralActivity';
import SuppliersDonutChart from './SuppliersDonutChart';
import TopSuppliers from './TopSuppliers';
import RiskGraphs from './RiskGraphs';

type CollateralResponse = {
  market_address: string;
  address: string;
  name: string;
  underlying_symbol: string;
  total_supply: string | null;
  total_supply_token: string | null;
  supply_cap: string | null;
  supply_cap_token: string | null;
  collateral_factor: string | null;
  liquidation_threshold: string | null;
  market_symbol: string;
  market_underlying_address: string;
  underlying_price: string | null;
  underlying_price_usd: string | null;
  total_supply_change: string | null;
  total_supply_change_percentage: string | null;
  total_supply_token_change: string | null;
  total_supply_token_change_percentage: string | null;
  utilization_change: string | null;
  utilization_change_percentage: string | null;
  collateral_factor_change: string | null;
  liquidation_threshold_change: string | null;
  supply_cap_change: string | null;
  supply_cap_change_precentage: string | null;
  supply_cap_token_change: string | null;
  supply_cap_token_change_percentage: string | null;
  underlying_price_change: string | null;
  underlying_price_change_percentage: string | null;
  underlying_price_usd_change: string | null;
  underlying_price_usd_change_percentage: string | null;
};

export default function CollateralPage() {
  const network = useNetwork() as string;
  const { marketAddress, collateralAddress } = useParams() as {
    marketAddress: string;
    collateralAddress: string;
  };
  const [daysAgo] = useSearchParams('days_ago', '1');

  const [data, error] = useApi<CollateralResponse>({
    url: `/markets/${marketAddress}/collaterals/${collateralAddress}/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <PageSkeleton>
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
        <ChartSkeleton />
        <ChartSkeleton />
        <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </Flex>
      </PageSkeleton>
    );
  }
  return (
    <Page
      title={
        <EntityName
          entitySize="4"
          symbol1={
            <CryptoIcon
              name={data.underlying_symbol}
              address={data.address}
              network={network}
              size="4"
            />
          }>
          <Heading>{data.name}</Heading>
        </EntityName>
      }>
      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <KpiCard
          title="Supply"
          data={data}
          footer={
            <Flex gap="xs">
              <CurrencyValue
                value={data.total_supply_token}
                options={{
                  iconSize: '1',
                  currency: {
                    name: data.market_symbol,
                    address: data.market_underlying_address,
                    network: network
                  }
                }}
              />
              <CurrencyValue
                value={data.total_supply_token_change}
                options={{
                  trend: true,
                  currency: {
                    name: data.market_symbol,
                    address: data.market_underlying_address,
                    network: network
                  },
                  tooltip: true,
                  tooltipProps: {
                    formatter: formatPercentage
                  }
                }}
              />
            </Flex>
          }
          field="total_supply"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.underlying_symbol,
                  address: data.address,
                  network: network
                },
                notation: 'compact'
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
        <KpiCard
          title="Supply Cap"
          data={data}
          footer={
            <Flex gap="xs">
              <CurrencyValue
                value={data.supply_cap_token}
                options={{
                  iconSize: '1',
                  currency: {
                    name: data.market_symbol,
                    address: data.market_underlying_address,
                    network: network
                  },
                  notation: 'compact'
                }}
              />
              <CurrencyValue
                value={data.supply_cap_token_change}
                options={{
                  currency: {
                    name: data.market_symbol,
                    address: data.market_underlying_address,
                    network: network
                  },
                  trend: true,
                  tooltip: true,
                  tooltipProps: {
                    formatter: formatPercentage
                  }
                }}
              />
            </Flex>
          }
          field="supply_cap"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.underlying_symbol,
                  address: data.address,
                  network: network
                },
                notation: 'compact'
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
        <KpiCard
          title="Utilization"
          data={data}
          field="utilization"
          options={{
            percentage: true,
            notation: 'compact'
          }}
        />
        <KpiCard
          title="Oracle Price"
          data={data}
          field="underlying_price"
          options={{
            valueOptions: {
              currencyValueOptions: {
                currencySize: '3',
                currency: {
                  name: data.market_symbol,
                  address: data.market_underlying_address,
                  network: network
                },
                notation: 'compact',
                formatCurrencyOptions: {
                  maximumFractionDigits:
                    Number(data.underlying_price) < 1.1 ? 5 : 2
                }
              }
            },
            valueTrendOptions: {
              currencyValueOptions: {
                trendIconSize: '0'
              }
            }
          }}
        />
      </Flex>

      <HistoricGraphs
        marketAddress={marketAddress}
        collateralAddress={collateralAddress}
        underlyingSymbol={data.underlying_symbol}
        network={network}
      />

      <Heading size="2">Collateral At Risk</Heading>

      <Flex gap={['s', 'xs']} flexDirection={['column', 'row']}>
        <Flex flexBasis="67%">
          <RiskGraphs
            marketAddress={marketAddress}
            collateralAddress={collateralAddress}
          />
        </Flex>
        <Flex flexBasis="33%" flexDirection="column" gap={['s', 'xs']}>
          <KpiCard
            title="Collateral Factor"
            data={data}
            field="collateral_factor"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
          />
          <KpiCard
            title="Liquidation Factor"
            data={data}
            field="liquidation_threshold"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
          />
          <KpiCard
            title="Liquidation Penalty"
            data={data}
            field="liquidation_penalty"
            options={{
              percentage: true,
              notation: 'compact',
              variants: {
                valuePercentageValue: 'dynamic'
              }
            }}
          />
        </Flex>
      </Flex>

      <CollateralActivity
        marketAddress={marketAddress}
        collateralAddress={collateralAddress}
      />
      <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
        <Flex flexBasis="40%">
          <TopSuppliers
            marketAddress={marketAddress}
            collateralAddress={collateralAddress}
            underlyingSymbol={data.underlying_symbol}
            network={network}
          />
        </Flex>
        <Flex
          flexBasis="60%"
          flexDirection="column"
          alignItems="center"
          gap="m">
          <Heading size="3">Share distribution</Heading>
          <SuppliersDonutChart />
        </Flex>
      </Flex>
    </Page>
  );
}
